/**
Copyright (C) 2019-2025, Infraclear Inc. All Rights Reserved.
*/

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import { Client, ClientTier, CreateClientPayload, UpdateClientPayload } from './types';
import { createClient, updateClient } from './clientService';

interface ClientFormDialogProps {
  open: boolean;
  client: Client | null;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const TIERS: ClientTier[] = ['BASIC', 'PRO', 'ENTERPRISE'];

const ClientFormDialog: React.FC<ClientFormDialogProps> = ({
  open,
  client,
  onClose,
  onSuccess,
  onError,
}) => {
  const isEdit = Boolean(client);

  const [organizationName, setOrganizationName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [tier, setTier] = useState<ClientTier>('BASIC');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (client) {
      setOrganizationName(client.organizationName);
      setContactEmail(client.contactEmail);
      setTier(client.tier);
      setDescription(client.description || '');
    } else {
      setOrganizationName('');
      setContactEmail('');
      setTier('BASIC');
      setDescription('');
    }
  }, [client, open]);

  const handleSubmit = () => {
    if (!organizationName.trim() || !contactEmail.trim()) return;

    setSubmitting(true);

    if (isEdit && client) {
      const payload: UpdateClientPayload = {
        id: client.id,
        organizationName: organizationName.trim(),
        contactEmail: contactEmail.trim(),
        tier,
        description: description.trim(),
      };
      updateClient(payload)
        .then(() => {
          onSuccess('Client updated successfully.');
          onClose();
        })
        .catch(() => {
          onError('Failed to update client.');
        })
        .finally(() => {
          setSubmitting(false);
        });
    } else {
      const payload: CreateClientPayload = {
        organizationName: organizationName.trim(),
        contactEmail: contactEmail.trim(),
        tier,
        description: description.trim(),
      };
      createClient(payload)
        .then(() => {
          onSuccess('Client created successfully.');
          onClose();
        })
        .catch(() => {
          onError('Failed to create client.');
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Client' : 'Create Client'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Organization Name"
            value={organizationName}
            onChange={(e) => setOrganizationName(e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label="Contact Email"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            required
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="client-tier-label">Tier</InputLabel>
            <Select
              labelId="client-tier-label"
              value={tier}
              label="Tier"
              onChange={(e) => setTier(e.target.value as ClientTier)}
            >
              {TIERS.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !organizationName.trim() || !contactEmail.trim()}
        >
          {isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientFormDialog;
